import type { IInputConfig } from './types';

import { merge } from '../../helper';

import addClass from '../../general/class/addClass';
import isEmptyArray from '../../general/isEmpty/isEmptyArray';
import isEmptyObject from '../../general/isEmpty/isEmptyObject';
import removeClass from '../../general/class/removeClass';
import setAttr from '../../general/attr/setAttr';

import * as exElement from '../../utils/element';
import * as exEvent from '../../utils/event';

import isNumberKey from './utils/isNumberKey';

// ----------------------------------------------------------------------------

function create(config: IInputConfig = {}): Record<string, any> {
    // Default Config
    const defConfig: IInputConfig = {
        pseudo: false,
    };

    // Merge
    merge(defConfig, config);

    // ------------------------------------------------------------------------

    // Switch Type
    switch (defConfig.type) {
        case 'datetime': // Custom type mapped to 'datetime-local'
            defConfig.type = 'datetime-local';
            break;
        case 'folder': // Custom type mapped to 'file' with directory attribute
            defConfig.type = 'file';
            defConfig.directory = true;
            break;
        case 'number':
            defConfig.number = true;
            break;
        case 'textarea':
            defConfig.tag = 'textarea';
            break;
        // No default
    }

    // ------------------------------------------------------------------------

    //
    const build: Record<string, any> = {
        elements: {},
    };

    // Create element (input)
    const elInput = exElement.create({
        merge: {
            ...defConfig,
            event: null,
        },
        tag: 'input',
        class: {
            new: 'form-control',
        },
    });
    build.elements.element = elInput;
    build.element = elInput;

    // ------------------------------------------------------------------------

    // Settings
    settings(build, defConfig);

    // Events
    events(build, defConfig);

    // Methods
    methods(build, defConfig);

    // ------------------------------------------------------------------------

    // Return
    return build;
}

function settings(build: Record<string, any>, config: Record<string, any> = {}): void {
    const elements = build.elements;

    // If using type
    if (config.type != null) {
        setAttr(elements.element, 'type', config.type);
    }

    // If using value
    if (config.value != null) {
        elements.element.value = config.value;
    }

    // If using name
    if (config.name != null) {
        setAttr(elements.element, 'name', config.name);
    }

    // If using placeholder
    if (config.placeholder != null) {
        setAttr(elements.element, 'placeholder', config.placeholder);
    }

    // If type is number
    if (config.type === 'number') {
        // If pseudo is false
        if (config.pseudo === false) {
            addClass(elements.element, 'hide-spin');
        }
    }

    // ------------------------------------------------------------------------

    // If using type file
    if (config.type === 'file') {
        // If using accept
        if (!isEmptyArray(config.accept)) {
            setAttr(elements.element, 'accept', config.accept.join(', '));
        }

        // If using directory
        if (config.directory === true) {
            setAttr(elements.element, 'webkitdirectory', '');
            setAttr(elements.element, 'directory', '');
        }

        // If using multiple
        if (config.multiple === true) {
            setAttr(elements.element, 'multiple', '');
        }
    }

    // ------------------------------------------------------------------------

    // If using required
    if (config.required === true || typeof config.required === 'string') {
        setAttr(elements.element, 'required', config.required === true ? '' : config.required);
    }
}

function events(build: Record<string, any>, config: Record<string, any> = {}): void {
    const elements = build.elements;

    // If using decimal
    if (typeof config.decimal === 'number') {
        config.number = true;

        // Create event (input)
        elements.element.addEventListener('input', function (this: HTMLInputElement): any {
            this.value = parseFloat(this.value).toFixed(config.decimal);
        });
    }

    // If using number
    if (config.number === true) {
        // Create event (keydown)
        elements.element.addEventListener('keydown', function (evt: KeyboardEvent): any {
            const isNumber = isNumberKey(evt, {
                preventDefault: true,
            });
            return isNumber;
        });

        // Create event (input)
        elements.element.addEventListener('input', function (this: HTMLInputElement): any {
            // If using max as max length
            if (this.max && this.value.length > this.max.length) {
                this.value = this.value.slice(0, this.max.length);
            }

            // If the value is greater than maximum
            if (parseInt(this.value) > parseInt(this.max)) {
                this.value = this.max;
            }
            // If the value is less than minimum
            else if (parseInt(this.value) < parseInt(this.min)) {
                this.value = this.min;
            }
        });
    }

    // If using required (Construction)
    if (!isEmptyObject(config.required)) {
        // Set attribute required message
        setAttr(elements.element, 'required', config.required.message ?? '');

        // If using required valid
        if (config.required.valid === true) {
            // Create event (input)
            elements.element.addEventListener('input', function (): any {
                elements.element.checkValidation();
            });

            // Create event (invalid)
            elements.element.addEventListener('invalid', function (): any {
                //
            });
        }
    }

    // ------------------------------------------------------------------------

    // If using event
    if (config.event != null) {
        exEvent.create(elements.element, config.event);
    }
}

function methods(build: Record<string, any>, config: Record<string, any> = {}): void {
    const elements = build.elements;

    // Function Check Validation (Construction)
    const fnCheckValidation = function (): any {
        const valid = elements.element.validity.valid;

        // Get element (form group)
        const elFormGroup = elements.element.closest('.form-group');
        // If there is element (form group)
        if (elFormGroup) {
            if (valid === false) {
                addClass(elFormGroup, 'has-error');
            } else {
                removeClass(elFormGroup, 'has-error');
            }
        }
        // Is default
        else {
            if (valid === false) {
                addClass(elements.element, 'has-error');
            } else {
                removeClass(elements.element, 'has-error');
            }
        }

        // Return
        return valid;
    };
    elements.element.checkValidation = fnCheckValidation;
}

// ----------------------------------------------------------------------------

export { create };
